/* eslint-disable react/jsx-no-useless-fragment */
import React from 'react';

import { directoryEntriesRoute } from '@lumapps/directories-entries/routes';
import { adminDirectoryTags } from '@lumapps/directories-tags/routes';
import { DEFAULT_DIRECTORY_MDI_ICON } from '@lumapps/directories/constants';
import { DIRECTORIES } from '@lumapps/directories/keys';
import { Directory } from '@lumapps/directories/types';
import { SideNavigationItem, type SideNavigationItemProps } from '@lumapps/lumx-back-office/components/SideNavigation';
import { useTranslate, GLOBAL } from '@lumapps/translations';

import { DIRECTORIES_ADMIN } from '../../keys';
import { directoriesRoute } from '../../routes';

export interface DirectoriesSideNavigationProps extends Partial<SideNavigationItemProps> {
    /** the list of directories to display */
    directories?: Directory[] | undefined;
    /** True if the current user is super admin */
    isSuperAdmin: boolean;
    /** True if the current user has access to the directories feature or not */
    isAllowed: boolean;
}

const DirectoriesSideNavigation: React.FC<DirectoriesSideNavigationProps> = (props) => {
    const { directories = [], isSuperAdmin = false, isAllowed = false, ...restOfProps } = props;
    const { translateKey, translateObject } = useTranslate();

    /**
     * If the user is superAdmin, we will render all directories and allow the user
     * to edit the content as well as the tags for the directory
     */
    if (isSuperAdmin) {
        return (
            <SideNavigationItem
                {...restOfProps}
                id="directories-menu"
                label={translateKey(GLOBAL.DIRECTORIES)}
                icon={DEFAULT_DIRECTORY_MDI_ICON}
            >
                <SideNavigationItem
                    id="all-directories"
                    label={translateKey(DIRECTORIES_ADMIN.ALL_DIRECTORIES)}
                    route={directoriesRoute}
                />
                {directories.map((directory: Directory) => (
                    <SideNavigationItem
                        id={`directories-${directory.id}`}
                        label={translateObject(directory.name) || ''}
                        key={directory.id}
                    >
                        <SideNavigationItem
                            id={`directories-${directory.id}-entries`}
                            label={translateKey(DIRECTORIES.CONTENTS)}
                            route={directoryEntriesRoute(directory.id)}
                        />
                        <SideNavigationItem
                            id={`directories-${directory.id}-tags`}
                            label={translateKey(GLOBAL.TAGS)}
                            route={adminDirectoryTags(directory.id)}
                        />
                    </SideNavigationItem>
                ))}
            </SideNavigationItem>
        );
    }

    if (isAllowed) {
        return (
            <SideNavigationItem
                {...restOfProps}
                id="directories-menu"
                label={translateKey(GLOBAL.DIRECTORIES)}
                icon={DEFAULT_DIRECTORY_MDI_ICON}
            >
                <SideNavigationItem
                    id="all-directories"
                    label={translateKey(DIRECTORIES_ADMIN.ALL_DIRECTORIES)}
                    route={directoriesRoute}
                />
                {directories.map((directory: Directory) => (
                    <SideNavigationItem
                        id={`directories-${directory.id}`}
                        label={translateObject(directory.name) || ''}
                        key={directory.id}
                        route={directoryEntriesRoute(directory.id)}
                    />
                ))}
            </SideNavigationItem>
        );
    }

    return null;
};

DirectoriesSideNavigation.displayName = 'SideNavigationItem';

export { DirectoriesSideNavigation };
